import { baseApi } from '@/lib/store/api/baseQuery'
import { apiEndpoints } from '@/lib/api/endpoints'
import { CareAction, GroupedCareAction, Prescription } from '@/types/interfaces/care'
import type { PrescriptionsPageData } from '@/types/interfaces/prescription'
import { LabDashboardItem } from '@/types/interfaces/lab'
import type { Visit } from '@/types/interfaces/appointment'

/** GET /api/external/dashboard/visits — the patient's full visit history. */
export interface DashboardVisits {
  upcoming_visits: Visit[]
  past_visits: Visit[]
}

/** NextStep::Questionnaire rows from GET /api/external/dashboard/show (renewal-criteria questionnaires excluded server-side). */
export interface IncompleteQuestionnaireNextStep {
  id: string
  care_id: string | null
  care_plan_attached: boolean
  state: string
  questionnaire_id: string
  questionnaire_name: string | null
  questionnaire_url: string
  deadline_at: string | null
  scheduled_at: string | null
}

export interface DashboardPrescription {
  id: string
  name?: string
  display_name?: string
  image_url?: string
  earliest_fill_date?: string | null
  has_pending_criteria?: boolean
  criteria_deadline?: string | null
  description?: string
  state?: string
  status?: string
  created_at?: string
  pharmacy_order_id?: string | null
  visit_booked_appointment_id?: string
  visit_booked_scheduled_time?: string
  visit_booked_appointment_type?: string
}

/** One entry from GET /api/external/dashboard/past_providers. */
export interface PastProvider {
  provider_id: string
  provider_name: string
  headshot_image_url: string | null
}

export interface PastProvidersResponse {
  providers: PastProvider[]
}

export interface DashboardInfo {
  recent_lab_orders: LabDashboardItem[]
  upcoming_visits: any[]
  recent_care_plans: any[]
  care_actions_sorted: Array<CareAction | GroupedCareAction | Prescription>
  future_visits: any[]
  previous_care_plans: any[]
  past_visits: any[]
  prescriptions?: DashboardPrescription[]
  past_lab_orders: LabDashboardItem[]
  has_completed_care_actions: boolean
  incomplete_questionnaire_next_steps?: IncompleteQuestionnaireNextStep[]
}

// Dashboard API slice
export const dashboardApi = baseApi.injectEndpoints({
  endpoints: (builder) => ({
    getOverview: builder.query<DashboardInfo, void>({
      query: () => apiEndpoints.dashboard.getOverview,
      providesTags: ['Dashboard'],
    }),
    getCarePlans: builder.query<any[], void>({
      query: () => apiEndpoints.dashboard.getCarePlans,
      providesTags: ['Dashboard'],
    }),
    getPrescriptions: builder.query<PrescriptionsPageData, void>({
      query: () => apiEndpoints.dashboard.getPrescriptions,
      providesTags: ['Dashboard'],
    }),
    getLabOrders: builder.query<any[], void>({
      query: () => apiEndpoints.dashboard.getLabOrders,
      providesTags: ['Dashboard'],
    }),
    getVisits: builder.query<DashboardVisits, void>({
      query: () => apiEndpoints.dashboard.getVisits,
      providesTags: ['Dashboard'],
    }),
    getPastProviders: builder.query<PastProvidersResponse, void>({
      query: () => apiEndpoints.dashboard.getPastProviders,
      providesTags: ['Dashboard'],
    }),
  }),
})

export const {
  useGetOverviewQuery,
  useGetCarePlansQuery,
  useGetPrescriptionsQuery,
  useGetLabOrdersQuery,
  useGetVisitsQuery,
  useGetPastProvidersQuery,
} = dashboardApi 